import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CampaignBasicInfo from '../../components/brand/CampaignBasicInfo';
import CampaignAssets from '../../components/brand/CampaignAssets';
import CampaignTargeting from '../../components/brand/CampaignTargeting';
import CampaignBudget from '../../components/brand/CampaignBudget';
import CampaignReview from '../../components/brand/CampaignReview';
import back_app from '../../lib/back_app';
import BrandHeader from '../../components/brand/BrandHeader';

interface CampaignData {
  name: string;
  description: string;
  category: string;
  selected_asset_ids: string[];
  landing_page?: string;
  allowed_ad_formats: string[];
  targeting: {
    countries: string[];
    age_ranges: number[][];
    genders: string[];
    content_categories: string[];
  };
  budget_info: {
    total_budget: number;
    cpm: number;
    currency: string;
    spent: number;
    remaining: number;
  };
  schedule: {
    start_date: string;
    end_date: string;
    is_always_on: boolean;
  };
}

const STEPS = [
  { id: 1, name: 'Basic Info', icon: '📝' },
  { id: 2, name: 'Assets', icon: '🎨' },
  { id: 3, name: 'Targeting', icon: '🎯' },
  { id: 4, name: 'Budget', icon: '💰' },
  { id: 5, name: 'Review', icon: '✓' },
];

export default function CreateCampaign() {
  const navigate = useNavigate();
  const { id: edit_id } = useParams();
  const [search_params, set_search_params] = useSearchParams();
  const initial_step = (() => {
    const s = parseInt(search_params.get('step') || '1', 10);
    return s >= 1 && s <= STEPS.length ? s : 1;
  })();
  const [currentStep, setCurrentStep] = useState(initial_step);

  useEffect(() => {
    const next = String(currentStep);
    if (search_params.get('step') != next) {
      const sp = new URLSearchParams(search_params);
      sp.set('step', next);
      set_search_params(sp, { replace: true });
    }
  }, [currentStep]);
  const [brandProfile, setBrandProfile] = useState<any>(null);
  const [draft_saved, set_draft_saved] = useState(false);
  const [is_saving_draft, set_is_saving_draft] = useState(false);
  const [draft_err, set_draft_err] = useState<string | null>(null);
  const [show_success, set_show_success] = useState(false);
  const draft_id_ref = useRef<string | null>(edit_id || null);
  const has_user_edited = useRef(false);
  const save_timeout_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [campaignData, setCampaignData] = useState<CampaignData>({
    name: '',
    description: '',
    category: '',
    selected_asset_ids: [],
    landing_page: '',
    allowed_ad_formats: [],
    targeting: {
      countries: [],
      age_ranges: [],
      genders: [],
      content_categories: [],
    },
    budget_info: {
      total_budget: 0,
      cpm: 0,
      currency: 'USD',
      spent: 0,
      remaining: 0,
    },
    schedule: {
      start_date: '',
      end_date: '',
      is_always_on: true,
    },
  });

  const save_draft = useCallback(async (draft_data: CampaignData) => {
    set_is_saving_draft(true);
    set_draft_saved(false);
    try {
      let payload: any = {
        name: draft_data.name || '',
        description: draft_data.description || '',
        category: draft_data.category || '',
        selected_asset_ids: draft_data.selected_asset_ids || [],
        landing_page: draft_data.landing_page || '',
        allowed_ad_formats: draft_data.allowed_ad_formats || ['break'],
        targeting: draft_data.targeting || {},
        budget_info: draft_data.budget_info || {},
        sched: draft_data.schedule || {},
        status: 'draft',
      };
      let res;
      if (draft_id_ref.current) {
        payload.id = draft_id_ref.current;
        res = await back_app.campaign_update(payload);
      } else {
        res = await back_app.campaign_create(payload);
      }
      console.log('draft save res', res);
      if (res?.err) {
        console.error('draft save error:', res.err);
        set_draft_err(res.err);
      } else if (res?.id) {
        draft_id_ref.current = res.id;
        set_draft_saved(true);
        set_draft_err(null);
        if (!edit_id) {
          const sp = new URLSearchParams(window.location.search);
          navigate(`/brand/create-campaign/${res.id}?${sp.toString()}`,
            { replace: true });
        }
      } else if (res?.ok) {
        set_draft_saved(true);
        set_draft_err(null);
      }
    } catch (e: any) {
      console.error('draft save failed', e);
      set_draft_err(e?.message || 'save failed');
    }
    set_is_saving_draft(false);
  }, []);

  const schedule_draft_save = useCallback((draft_data: CampaignData) => {
    if (save_timeout_ref.current)
      clearTimeout(save_timeout_ref.current);
    save_timeout_ref.current = setTimeout(() => {
      save_draft(draft_data);
    }, 1500);
  }, [save_draft]);

  useEffect(() => {
    return () => {
      if (save_timeout_ref.current)
        clearTimeout(save_timeout_ref.current);
    };
  }, []);

  useEffect(() => {
    if (!edit_id)
      return;
    (async () => {
      try {
        let res = await back_app.campaign_get(edit_id);
        if (res && !res.err) {
          let sched = res.sched || res.schedule || {};
          let to_ymd = (d: any) => d instanceof Date
            ? d.toISOString().slice(0, 10)
            : (d || '');
          setCampaignData(prev => ({...prev, ...res,
            schedule: {...prev.schedule, ...sched,
              start_date: to_ymd(sched.start_date),
              end_date: to_ymd(sched.end_date)}}));
          draft_id_ref.current = edit_id;
        }
      } catch (e) {
        console.error('failed to load draft', e);
      }
    })();
  }, [edit_id]);

  useEffect(() => {
    fetchBrandProfile();
  }, []);

  const fetchBrandProfile = async () => {
    try {
      const result = await back_app.bprofile_get();
      if (!result.err && result) {
        setBrandProfile(result);
        if (result.industry) {
          setCampaignData(prev => ({
            ...prev,
            category: result.industry
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const updateCampaignData = (field: string, value: any) => {
    has_user_edited.current = true;
    set_draft_saved(false);
    set_draft_err(null);
    setCampaignData((prev) => {
      let next = {...prev, [field]: value};
      schedule_draft_save(next);
      return next;
    });
  };

  const flush_draft_save = () => {
    if (save_timeout_ref.current) {
      clearTimeout(save_timeout_ref.current);
      save_timeout_ref.current = null;
    }
    if (has_user_edited.current)
      save_draft(campaignData);
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) {
      flush_draft_save();
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      flush_draft_save();
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

 const handleSubmit = async () => {
  if (save_timeout_ref.current) {
    clearTimeout(save_timeout_ref.current);
    save_timeout_ref.current = null;
  }
  try {
    if (!campaignData.selected_asset_ids || campaignData.selected_asset_ids.length === 0) {
      alert('Please select at least one asset for your campaign');
      setCurrentStep(2);
      return;
    }

    if (!campaignData.allowed_ad_formats || campaignData.allowed_ad_formats.length === 0) {
      campaignData.allowed_ad_formats = ['break'];
    }

    let payload: any = {
      name: campaignData.name,
      description: campaignData.description,
      category: campaignData.category,
      selected_asset_ids: campaignData.selected_asset_ids,
      landing_page: campaignData.landing_page,
      allowed_ad_formats: campaignData.allowed_ad_formats || ['break'],
      targeting: campaignData.targeting,
      budget_info: campaignData.budget_info,
      sched: campaignData.schedule || {},
      status: 'active',
    };
    let result;
    if (draft_id_ref.current) {
      payload.id = draft_id_ref.current;
      result = await back_app.campaign_update(payload);
    } else {
      result = await back_app.campaign_create(payload);
    }

    
    if (result?.id)
      draft_id_ref.current = result.id;
    if (!result.err && draft_id_ref.current) {
      set_show_success(true);
    } else {
      let err_map: Record<string, string> = {
        brand_account_not_found: 'Add funds to your account (Profile) before launching. Campaign saved as draft.',
        insufficient_funds: 'Your account balance is below the campaign budget. Add funds in Profile. Campaign saved as draft.',
        invalid_budget: 'Please set a total budget for this campaign.',
      };
      alert('Error: ' + (err_map[result.err] || result.err || 'Failed to create campaign'));
    }
  } catch (error) {
    console.error('Error:', error);
    if ((error as Error)?.message == 'no session') alert('Not authenticated');
    else alert('Error creating campaign');
  }
};

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      {}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">
              {edit_id ? 'Edit Campaign' : 'Create New Campaign'}
            </h2>
            {is_saving_draft && (
              <span className="text-sm text-white/70 animate-pulse">Saving draft...</span>
            )}
            {!is_saving_draft && draft_saved && (
              <span className="text-sm bg-white/20 px-2 py-1 rounded">Draft saved</span>
            )}
            {!is_saving_draft && draft_err && (
              <span className="text-sm bg-red-500/80 px-2 py-1 rounded">Draft error: {draft_err}</span>
            )}
          </div>
          <p className="text-white/90">
            Set up your campaign in {STEPS.length} easy steps
          </p>
        </div>
      </div>

      {}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          {}
          <div className="hidden md:flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      currentStep === step.id
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white scale-110 shadow-lg'
                        : currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.id ? '✓' : step.id}
                  </div>
                  <span className={`text-xs mt-2 font-semibold ${
                    currentStep === step.id ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className="flex-1 h-1 mx-4 -mt-8">
                    <div className={`h-full rounded-full transition-all ${
                      currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Step {currentStep} of {STEPS.length}
              </span>
              <span className="text-sm text-gray-600">
                {STEPS[currentStep - 1].name}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 lg:p-8">
          {currentStep === 1 && (
            <CampaignBasicInfo
              data={campaignData}
              brandProfile={brandProfile}
              onChange={updateCampaignData}
              onNext={nextStep}
            />
          )}

          {currentStep === 2 && (
            <CampaignAssets
              data={campaignData}
              onChange={updateCampaignData}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}

          {currentStep === 3 && (
            <CampaignTargeting
              data={campaignData}
              onChange={updateCampaignData}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}

          {currentStep === 4 && (
            <CampaignBudget
              data={campaignData}
              onChange={updateCampaignData}
              onNext={nextStep}
              onBack={prevStep}
            />
          )}

          {currentStep === 5 && (
            <CampaignReview
              data={campaignData}
              onSubmit={handleSubmit}
              onBack={prevStep}
              onEdit={(step) => setCurrentStep(step)}
            />
          )}
        </div>
      </div>

      {show_success && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Campaign created</h2>
            <p className="text-gray-600 mb-6">
              Your campaign is live and ready for creators to discover.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => set_show_success(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Stay here
              </button>
              <button
                onClick={() => navigate('/brand/campaigns')}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Go to Campaigns
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}